import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Step,
  StepButton,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import MultiSelectInput from "../MultiSelectInput";
import { statesList } from "../../lib/constants";
import carrierService from "../../services/carrier.service";
import { addStateDocusignService } from "../../services/docusign.service";
import addStateRequestService from "../../services/add-state-request.service";
import { useAuth } from "../../hooks/useAuth";

// #19 — Add State, built like the contract request: a step flow ending in a real
// DocuSign signing step (jump out, sign, return to /add-state/signing-return/:id,
// then Review and submit). Identity / additional details come from the agent's
// profile (onboarding); this flow only asks the request-specific bits.

const Display = ({ label, value }) => (
  <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
    <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
      {label}
    </TableCell>
    <TableCell>{value || "-"}</TableCell>
  </TableRow>
);

// Step 1 — Eligibility gate: you can only add a state you are licensed in.
const Eligibility = ({ data, completeStep }) => {
  const [licensed, setLicensed] = useState(data.licensed || "");
  return (
    <>
      <FormControl>
        <FormLabel>
          Do you currently have a license in the state(s) you are adding?
        </FormLabel>
        <RadioGroup
          row
          value={licensed}
          onChange={(e) => setLicensed(e.target.value)}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
      </FormControl>
      {licensed === "no" && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          To add a state, you will first need to obtain a license in the state
          you would like to add.
        </Alert>
      )}
      <Button
        fullWidth
        variant="contained"
        sx={{ mt: 2 }}
        disabled={licensed !== "yes"}
        onClick={() => completeStep({ licensed })}
      >
        Next
      </Button>
    </>
  );
};

// Step 2 — which state(s) and carrier(s).
const StateCarriers = ({ data, completeStep, carrierOptions }) => (
  <Formik
    initialValues={{ states: data.states || [], carriers: data.carriers || [] }}
    validationSchema={Yup.object().shape({
      states: Yup.array().min(1, "Select at least one state to add"),
      carriers: Yup.array().min(1, "Select at least one carrier"),
    })}
    onSubmit={(values) => completeStep(values)}
  >
    <Form>
      <Field
        name="states"
        label="Which state(s) are you adding?"
        component={MultiSelectInput}
        variant="outlined"
        options={statesList}
      />
      <Field
        name="carriers"
        label="Which carrier(s)?"
        component={MultiSelectInput}
        variant="outlined"
        options={carrierOptions}
      />
      <Button type="submit" fullWidth variant="contained" sx={{ mt: 1 }}>
        Next
      </Button>
    </Form>
  </Formik>
);

// Step 3 — DocuSign. Real jump-out: sign the add-state form (and, in production,
// upload the license), then DocuSign returns to /add-state/signing-return/:id.
const SignStep = ({ data, user, agentInfo }) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const agent = agentInfo || user?.agent || {};

  const goToSigning = () => {
    setLoading(true);
    addStateDocusignService
      .startSigning({
        ...data,
        // identity for the signer (and for resume) — server is authoritative.
        agent: agent.id,
        npn: agent.npn,
        firstName: agent.firstName,
        lastName: agent.lastName,
        email: agent.email,
      })
      .then(({ signingUrl }) => {
        window.location.href = signingUrl;
      })
      .catch((err) => {
        setErrorMessage(
          `There was a problem starting the signing process: ${
            err?.response?.data?.message || err.message
          }`
        );
        setLoading(false);
      });
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Paper sx={{ p: 4, maxWidth: 560, textAlign: "center" }} elevation={2}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="h6">Sign your add-state form</Typography>
          <Typography variant="body1" color="text.secondary">
            Next, you'll be taken to DocuSign, an external site, to sign your
            add-state form and upload your license for the new state. This must be
            completed before you can review and submit.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When you finish signing, you'll be brought back here automatically.
          </Typography>
          <Button
            variant="contained"
            size="large"
            disabled={loading}
            onClick={goToSigning}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {loading ? "Taking you to DocuSign..." : "Continue to DocuSign"}
          </Button>
        </Stack>
        {errorMessage && (
          <Snackbar
            open={!!errorMessage}
            onClose={() => setErrorMessage(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert onClose={() => setErrorMessage(null)} severity="error">
              {errorMessage}
            </Alert>
          </Snackbar>
        )}
      </Paper>
    </Box>
  );
};

// Step 4 — review (agent identity, sourced from the profile, plus the request)
// then submit.
const ReviewStep = ({ data, completeStep, carrierOptions, user, agentInfo }) => {
  const agent = agentInfo || user?.agent || {};
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const stateNames = (data.states || []).map(
    (id) => statesList.find((s) => s.id === id)?.name || id
  );
  const carrierNames = (data.carriers || []).map(
    (id) => carrierOptions.find((c) => c.id === id)?.name || id
  );
  const cityStateZip = [agent.city, agent.state, agent.zip]
    .filter(Boolean)
    .join(", ");

  const submit = () => {
    setSubmitting(true);
    addStateRequestService
      .request({
        states: data.states,
        carriers: data.carriers,
        unsignedId: data.unsignedId,
        onBehalfOfAgent: data.onBehalfOfAgent,
      })
      .then(() => completeStep({}))
      .catch((err) => {
        setErrorMessage(
          `There was a problem submitting your request: ${
            err?.response?.data?.message || err.message
          }`
        );
        setSubmitting(false);
      });
  };

  return (
    <>
      <Typography variant="h6">Please review your add-state request:</Typography>
      <TableContainer sx={{ mt: 1 }} component={Paper}>
        <Table size="small">
          <TableBody>
            <Display label="NPN" value={agent.npn} />
            <Display label="First Name" value={agent.firstName} />
            <Display label="Last Name" value={agent.lastName} />
            <Display label="E-Mail" value={agent.email} />
            <Display label="Phone" value={agent.phone} />
            <Display label="Address" value={agent.address} />
            <Display label="City / State / Zip" value={cityStateZip} />
            <Display label="States to add" value={stateNames.join(", ")} />
            <Display label="Carriers" value={carrierNames.join(", ")} />
          </TableBody>
        </Table>
      </TableContainer>
      <Button
        fullWidth
        variant="contained"
        sx={{ mt: 2 }}
        disabled={submitting}
        onClick={submit}
      >
        Submit request
      </Button>
      {errorMessage && (
        <Snackbar
          open={!!errorMessage}
          onClose={() => setErrorMessage(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={() => setErrorMessage(null)} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

const Confirmation = () => (
  <Alert severity="success">
    Your request to add the selected state(s) has been recorded. We'll be in
    touch with next steps.
  </Alert>
);

const STEP_LIST = [
  { step: 1, label: "Eligibility", component: Eligibility },
  { step: 2, label: "State & Carriers", component: StateCarriers },
  { step: 3, label: "DocuSign", component: SignStep },
  { step: 4, label: "Review", component: ReviewStep },
  { step: 5, label: "Confirmation", component: Confirmation },
];

// initialData/initialStep/unsignedId let /add-state/signing-return/:id rehydrate
// the flow at Review after the agent returns from signing (#11-style resume).
const AddState = ({ initialData = null, initialStep = 1, unsignedId = null, agentOverride = null }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [step, setStep] = useState(initialStep);
  const [data, setData] = useState(() => ({
    ...(initialData || {}),
    ...(unsignedId ? { unsignedId } : {}),
  }));

  useEffect(() => {
    let active = true;
    carrierService
      .list(user)
      .then((d) => {
        if (active) {
          setCarrierOptions(
            (d || []).map((c) => ({ id: c.id || c.value, name: c.value || c.label }))
          );
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [user]);

  const completeStep = (stepData) => {
    setData((prev) => ({ ...prev, ...stepData }));
    setStep((s) => s + 1);
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const Content = STEP_LIST[step - 1].component;

  return (
    <Box sx={{ maxWidth: 720, margin: "0 auto" }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/")}
        sx={{ mb: 1 }}
      >
        Back to dashboard
      </Button>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Add a State
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Request appointment with carriers in a state you are newly licensed in.
      </Typography>
      {/* #57 - white glove: a principal is preparing this request for one of
          their agents. Rides the payload so it survives the signing round-trip. */}
      {data.onBehalfOfAgent && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You are preparing this request on behalf of{" "}
          {[agentOverride?.firstName, agentOverride?.lastName]
            .filter(Boolean)
            .join(" ") || "your agent"}
          .
        </Alert>
      )}
      <Stepper nonLinear activeStep={step - 1} sx={{ pb: 4 }}>
        {STEP_LIST.slice(0, -1).map((s) => (
          <Step key={s.step} completed={s.step < step}>
            <StepButton
              color="inherit"
              onClick={() => s.step < step && setStep(s.step)}
            >
              {s.label}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      <Content
        data={data}
        completeStep={completeStep}
        carrierOptions={carrierOptions}
        user={user}
        agentInfo={agentOverride}
      />
      {step > 1 && step < STEP_LIST.length && step !== 3 && (
        <Button
          variant="contained"
          color="secondary"
          sx={{ mt: 1 }}
          onClick={goBack}
        >
          Back
        </Button>
      )}
    </Box>
  );
};

export default AddState;
